import { useState, useEffect } from 'react'
import type CrmFundraisingRecurringDonation from '@/types/serializers/CrmFundraisingRecurringDonation'
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import RecurringDonationShow from '../Show'

interface ShowProps {
  recurring_donation: CrmFundraisingRecurringDonation
}

interface RecurringDetailSheetProps {
  recurringDonationId: number | null
  onOpenChange: (open: boolean) => void
}

export function RecurringDetailSheet({ recurringDonationId, onOpenChange }: RecurringDetailSheetProps) {
  const [data, setData] = useState<ShowProps | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!recurringDonationId) {
      setData(null)
      setError(false)
      return
    }
    // `active` guards every state write so a superseded request (selection
    // changed mid-flight) can't flip loading/data/error for the current one.
    let active = true
    setLoading(true)
    setError(false)

    const controller = new AbortController()
    fetch(`/fundraising/recurring_donations/${recurringDonationId}`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load')
        return res.json()
      })
      .then((json) => { if (active) setData(json) })
      .catch((err) => {
        if (active && err.name !== 'AbortError') setError(true)
      })
      .finally(() => { if (active) setLoading(false) })

    return () => {
      active = false
      controller.abort()
    }
  }, [recurringDonationId, reloadKey])

  return (
    <Sheet open={!!recurringDonationId} onOpenChange={onOpenChange}>
      <SheetContent className="w-[700px] sm:max-w-[700px] overflow-y-auto p-0" aria-describedby={undefined}>
        <VisuallyHidden.Root>
          <SheetTitle>Détail du don récurrent</SheetTitle>
        </VisuallyHidden.Root>
        {loading ? (
          <div className="space-y-4 p-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : error ? (
          <div className="space-y-3 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              Impossible de charger le don récurrent.
            </p>
            <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
              Réessayer
            </Button>
          </div>
        ) : data ? (
          <div className="p-6">
            <RecurringDonationShow
              recurring_donation={data.recurring_donation}
              inSheet
              onMutated={() => setReloadKey((k) => k + 1)}
              onClose={() => onOpenChange(false)}
            />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
